import "./RsvpForm.css";
import { useSearchParams } from "react-router-dom";
import { StartingPoint } from "../../interfaces/StartingPoint.interface";
import {
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Http } from "../../utilities/Http";
import { OnRsvpModal } from "../OnRsvpModal/OnRsvpModal";
import { CreateRsvpResponse } from "../../interfaces/CreateRsvpResponse.interface";
import { getStartingPoint } from "../../utilities/helpers/getStartingPointName";
import { RsvpDetails } from "../../interfaces/RsvpDetails.interface";
import { ApiRoutes } from "../../constants/apiRoutes";
import { CreateRsvpRequest } from "../../interfaces/CreateRsvpRequest.interface";
import { UpdateRsvpRequest } from "../../interfaces/UpdateRsvpRequest.interface";

interface RsvpFormProps {
  http: Http;
  startingPoints: StartingPoint[];
  reloadRsvps: () => void;
}

const NO_STARTING_POINT = "-";

export const RsvpForm = ({
  http,
  startingPoints,
  reloadRsvps,
}: RsvpFormProps) => {
  let [searchParams, setSearchParams] = useSearchParams();

  const [isPostLoading, setIsPostLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [submittedStartingPoint, setSubmittedStartingPoint] =
    useState<StartingPoint | null>(null);
  const [existingRsvp, setExistingRsvp] = useState<RsvpDetails | null>(null);

  const [nameError, setNameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [startingPointIdError, setStartingPointIdError] = useState<
    string | null
  >(null);

  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [startingPointId, setStartingPointId] =
    useState<string>(NO_STARTING_POINT);
  const [isAttending, setIsAttending] = useState<boolean>(true);

  useEffect(() => {
    const getRsvpDetails = async () => {
      const token = searchParams.get("edit-token")?.trim();

      if (token) {
        try {
          const rsvpDetails = await http.get<RsvpDetails>(
            `${ApiRoutes.GetRsvpDetails}?edit-token=${token}`
          );
          setExistingRsvp(rsvpDetails);
          setName(rsvpDetails.name);
          setEmail(rsvpDetails.email);
          setPhone(rsvpDetails.phone || "");
          setIsAttending(rsvpDetails.is_attending);
          setStartingPointId(
            rsvpDetails.starting_point_id || NO_STARTING_POINT
          );
        } catch (error) {
          setSearchParams(new URLSearchParams());
        }
      }
    };

    getRsvpDetails();
  }, []);

  const onSubmit = async () => {
    const hasError = validate();

    if (!hasError) {
      await submit();
    }
  };

  const validate = (): boolean => {
    let hasError = false;

    if (name.length === 0) {
      setNameError("Name is required");
      hasError = true;
    }

    if (email.length === 0) {
      setEmailError("Email is required");
      hasError = true;
    } else if (!email.includes(".") || !email.includes("@")) {
      setEmailError("Invalid email address");
      hasError = true;
    }

    if (isAttending && phone.length === 0) {
      setPhoneError("Phone number is required");
      hasError = true;
    }

    if (isAttending && startingPointId === NO_STARTING_POINT) {
      setStartingPointIdError(
        'Starting point is required. Select "Finish Line" if you don\'t plan on running.'
      );
      hasError = true;
    }

    return hasError;
  };

  const createRsvp = async (request: CreateRsvpRequest) => {
    return http.post<CreateRsvpRequest, CreateRsvpResponse>(
      ApiRoutes.CreateRsvp,
      request
    );
  };

  const updateRsvp = async (request: UpdateRsvpRequest) => {
    return http.put<CreateRsvpRequest, CreateRsvpResponse>(
      ApiRoutes.UpdateRsvp,
      request
    );
  };

  const submit = async () => {
    setIsPostLoading(true);

    try {
      const request = {
        name,
        email,
        phone: isAttending ? phone : null,
        starting_point_id:
          startingPointId === NO_STARTING_POINT ? null : startingPointId,
        is_attending: isAttending,
      };

      const response = existingRsvp
        ? await updateRsvp({
            ...request,
            email: existingRsvp.email,
            edit_token: searchParams.get("edit-token")!.trim(),
          })
        : await createRsvp(request);

      const startingPoint = getStartingPoint(
        startingPoints,
        response.starting_point_id
      );

      setSearchParams({
        "edit-token": response.edit_token,
      });
      reloadRsvps();
      setSubmittedStartingPoint(startingPoint);
      setExistingRsvp(response);
      setIsModalOpen(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsPostLoading(false);
    }
  };

  return (
    <>
      <div>
        <h2>RSVP</h2>
        <div className="rsvpForm">
          <FormControl>
            <ToggleButtonGroup
              className="isAttendingToggle"
              value={isAttending ? "yes" : "no"}
              exclusive
              onChange={(_event, newRsvp: string) => {
                const isAttending = newRsvp === "yes";
                setIsAttending(isAttending);
                if (!isAttending) {
                  setStartingPointId(NO_STARTING_POINT);
                }
              }}
              sx={{ boxShadow: 1 }}
            >
              <ToggleButton value="yes">Yes! 🎉</ToggleButton>
              <ToggleButton value="no">No 😭</ToggleButton>
            </ToggleButtonGroup>
          </FormControl>
          <FormControl>
            <TextField
              id="outlined-basic"
              label="Name"
              variant="outlined"
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setNameError(null);
              }}
              error={!!nameError}
              helperText={nameError}
            />
          </FormControl>
          <FormControl>
            <TextField
              id="outlined-basic"
              label="Email"
              variant="outlined"
              value={email}
              disabled={!!existingRsvp}
              onChange={(event) => {
                setEmail(event.target.value);
                setEmailError(null);
              }}
              error={!!emailError}
              helperText={emailError}
            />
          </FormControl>
          {isAttending && (
            <FormControl>
              <TextField
                id="outlined-basic"
                label="Phone"
                variant="outlined"
                value={phone}
                onChange={(event) => {
                  setPhone(event.target.value);
                  setPhoneError(null);
                }}
                error={!!phoneError}
                helperText={phoneError}
              />
            </FormControl>
          )}
          {isAttending && (
            <FormControl error={!!startingPointIdError}>
              <InputLabel id="startingPointSelectLabel">
                Starting Point
              </InputLabel>
              <Select
                disabled={!isAttending}
                labelId="startingPointSelectLabel"
                id="startingPointSelect"
                value={startingPointId}
                label="Starting Point"
                onChange={(event) => {
                  setStartingPointId(event.target.value);
                  setStartingPointIdError(null);
                }}
                error={!!startingPointIdError}
              >
                <MenuItem
                  key={"no-selected"}
                  value={NO_STARTING_POINT}
                  disabled
                >
                  {NO_STARTING_POINT}
                </MenuItem>
                {startingPoints.map((startingPoint) => (
                  <MenuItem
                    key={startingPoint.id}
                    value={startingPoint.id}
                  >{`${startingPoint.name} (${startingPoint.miles} miles)`}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{startingPointIdError}</FormHelperText>
            </FormControl>
          )}

          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={isPostLoading}
          >
            {existingRsvp && "Update"} RSVP!
          </Button>
        </div>
      </div>
      <OnRsvpModal
        isModalOpen={isModalOpen}
        rsvpResponse={existingRsvp!}
        startingPoint={submittedStartingPoint}
        onClose={() => {
          setIsModalOpen(false);
          setSubmittedStartingPoint(null);
        }}
      />
    </>
  );
};
